import React, { useState, useMemo } from 'react';
import { sendToTelegram } from '../../data/telegram';
import { Modal } from './Modal';
import { useLang } from '../../i18n';
import { printScoped } from './dom';

/**
 * Generic preview / print / share / Telegram sheet. Renders `sections` as a clean
 * printable doc you can screenshot, print, or send. Each section:
 * { key, heading, count?, rows:[{ name, meta?, done? }] }. Chips are opt-in: with none
 * picked everything is shared; pick one or more to share just those. `subtitle` may be
 * a function of the shown row count so the "N still needed" line matches the selection. Reuses the .do-print__* styles (so `body.do-printing`
 * prints just this).
 */
export function PreviewModal({ title, subtitle, sections, onClose, notify }) {
  const { t } = useLang();
  const [picked, setPicked] = useState(() => new Set()); // section keys the user picked; empty = share everything
  const [tg, setTg] = useState('idle');                  // idle | sending | sent
  const [zoom, setZoom] = useState(1);                   // enlarge/shrink the list for reading & screenshots
  const zoomBy = d => setZoom(z => Math.min(1.8, Math.max(0.6, Math.round((z + d) * 100) / 100)));

  // Ignore picks for sections that have since gone (their last item was ticked off).
  const active = useMemo(() => sections.filter(s => picked.has(s.key)), [sections, picked]);
  const shown = active.length ? active : sections;
  const toggle = key => setPicked(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const sub = typeof subtitle === 'function' ? subtitle(shown.reduce((n, s) => n + s.rows.length, 0)) : subtitle;

  const text = useMemo(() => {
    let out = `${title} — ${sub}\n`;
    shown.forEach(s => {
      out += `\n${s.heading}${s.count != null ? ` (${s.count})` : ''}:\n`;
      s.rows.forEach(r => { out += `${r.done ? '✓' : '•'} ${r.name}${r.meta ? ` — ${r.meta}` : ''}\n`; });
    });
    if (!shown.length) out += `\n${t('preview.nothingToShow')}\n`;
    return out;
  }, [title, sub, shown, t]);

  const copy = async () => { try { await navigator.clipboard.writeText(text); notify(t('preview.copied')); } catch { /* no clipboard */ } };
  const sendTg = async () => {
    if (!shown.length || tg === 'sending') return;
    setTg('sending');
    try {
      await sendToTelegram(title, text);
      setTg('sent');
      notify(t('preview.tgSent'));
      setTimeout(() => setTg('idle'), 2500);
    } catch (e) {
      setTg('idle');
      notify(e.message || t('preview.tgFailed'));
    }
  };
  // Open WhatsApp with the list pre-filled — you pick the group or a contact and
  // send. Works for groups (unlike automated WhatsApp API sends) and needs no setup.
  const shareWhatsApp = () => {
    if (!shown.length) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal className="do-modal--wide" label={title} onClose={onClose}>
      <div className="do-print__doc" style={{ zoom }}>
        <h2 className="do-print__title">{title}</h2>
        <p className="do-print__date">{sub}</p>
        {sections.length > 1 && (
          <div className="do-prev-pick" role="group" aria-label={t('preview.pickLabel')}>
            <div className="do-prev-pick__status">
              {active.length
                ? t('preview.sharingSome', { n: active.length, total: sections.length })
                : t('preview.sharingAll')}
            </div>
            <div className="do-prev-chips">
              {sections.map(s => {
                const on = picked.has(s.key);
                return (
                  <button key={s.key} type="button" className={`do-prev-chip${on ? ' do-prev-chip--on' : ''}`}
                    onClick={() => toggle(s.key)} aria-pressed={on}>
                    <span className="do-prev-chip__tick" aria-hidden="true">✓</span>
                    <span>{s.heading}</span>
                    {s.count != null && <span className="do-prev-chip__count">{s.count}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {shown.length === 0 ? (
          <p className="do-print__empty">{t('preview.nothingToShow')}</p>
        ) : shown.map(s => (
          <div key={s.key} className="do-print__sec">
            <h3 className="do-print__sec-title">{s.heading}{s.count != null ? ` · ${s.count}` : ''}</h3>
            <ul className="do-print__list">
              {s.rows.map((r, i) => (
                <li key={i} className={`do-print__row${r.done ? ' do-print__row--done' : ''}`}>
                  <span className={`do-print__box${r.done ? ' do-print__box--done' : ''}`}>{r.done ? '✓' : ''}</span>
                  <span className="do-print__name">{r.name}</span>
                  {r.meta && <span className="do-print__qty">{r.meta}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="do-print__actions">
        <div className="do-zoom" role="group" aria-label={t('preview.zoomGroup')}>
          <button className="do-zoom__btn" onClick={() => zoomBy(-0.1)} disabled={zoom <= 0.6} aria-label={t('preview.zoomOut')} title={t('preview.zoomOut')}>－</button>
          <button className="do-zoom__val" onClick={() => setZoom(1)} title={t('preview.zoomReset')}>{Math.round(zoom * 100)}%</button>
          <button className="do-zoom__btn" onClick={() => zoomBy(0.1)} disabled={zoom >= 1.8} aria-label={t('preview.zoomIn')} title={t('preview.zoomIn')}>＋</button>
        </div>
        <button className="do-modal__btn" onClick={onClose}>{t('common.close')}</button>
        <button className="do-modal__btn" onClick={copy}>{t('preview.copy')}</button>
        <button className="do-modal__btn" onClick={printScoped}>{t('preview.print')}</button>
        <button className="do-modal__btn do-modal__btn--wa" onClick={shareWhatsApp} disabled={!shown.length}
          title={t('preview.whatsappTitle')}>{t('preview.whatsapp')}</button>
        <button className="do-modal__btn do-modal__btn--primary do-modal__btn--tg" onClick={sendTg}
          disabled={tg === 'sending' || !shown.length}>
          {tg === 'sending' ? t('preview.tgSending') : tg === 'sent' ? t('preview.tgDone') : t('preview.tgSend')}
        </button>
      </div>
    </Modal>
  );
}
